import type {
    LookupCreditCardFeeResponse,
    LookupCreditCardFeeSchemaResponse,
} from '@getstep/proto/acquiring'
import type { Amount } from '@getstep/proto/common'
import type { UnauthenticatedTransferRequest } from '@getstep/proto/transfer'
import { addAmounts } from '@getstep/sdk/dist/http/models/Amount'
import { AssertionFailure } from '@getstep/sdk/dist/util/Assert'
import { CustomModalId } from '@graphql/hooks/modal'
import type { Personalizations } from '@lib/hooks/usePersonalizations'
import type { LinkingStore } from '@lib/store/linking/LinkingStore'
import {
    makePersistentObservable,
    PersistentStore,
} from '@lib/store/persistence/PersistentStore'
import type { SdkClientStore } from '@lib/store/SdkClientStore'
import { modalStore } from '@lib/store/ui/modal'
import { formatAmount, formatPercent } from '@lib/utils/number'
import type {
    PaymentAddress,
    PaymentContactInfo,
    PaymentFormValues,
    TokenizedCard,
} from '@lib/utils/payment'
import { CardTypeForDisplay, formatCard } from '@lib/utils/payment'
import { TEXT } from '@lib/utils/text'
import { captureException } from '@sentry/react'
import { extendConsole } from '@shared/logging/console'
import { LINK } from '@shared/util/link'
import type { Stripe, StripeCardNumberElement } from '@stripe/stripe-js'
import { computed, observable } from 'mobx'
import type { NextRouter } from 'next/router'

import { PaymentIntentStage } from './PaymentIntentStage'

const logger = extendConsole('PaymentIntentStore')

type Fees = LookupCreditCardFeeSchemaResponse & LookupCreditCardFeeResponse

/**
 * Manages the P2P card payment flow.
 * Persists contact info and card details, in case the page is reloaded.
 */
export class PaymentIntentStore extends PersistentStore {
    static readonly className = 'PaymentIntentStore'
    static readonly persistentFields = [
        'contactInfo',
        'transferRequestId',
        'card',
        'attempts',
        'fees',
        'authorizationUrl',
    ]

    /**
     * Number of times the user has attempted to pay.
     */
    attempts = 0

    /**
     * Transaction fee info.
     */
    fees!: Fees

    /**
     * Transfer amount, not including fees.
     */
    amount!: Amount

    /**
     * Associated transfer request id.
     */
    transferRequestId!: string

    /**
     * Payer's contact info.
     */
    contactInfo: PaymentContactInfo = {
        fullName: '',
        email: '',
    }

    /**
     * Parts of payer's address.
     */
    addressInfo: PaymentAddress = {
        zipPostalCode: '',
    }

    /**
     * Card info fit for display.
     */
    card: TokenizedCard = {
        last4: '',
        token: '',
        type: CardTypeForDisplay.Other,
    }

    stripe?: { sdk: Stripe; cardNumberElement: StripeCardNumberElement }

    /**
     * The URL for 3DS authorization if required for the transfer
     */
    authorizationUrl = ''

    private constructor(
        private sdk: SdkClientStore,
        transferRequestId?: string,
        fees?: Fees,
        amount?: Amount,
    ) {
        super()

        makePersistentObservable(this, {
            authorizationUrl: observable,
            contactInfo: observable.deep,
            addressInfo: observable.deep,
            card: observable.deep,
            formattedCard: computed,
        })

        if (fees) {
            this.fees = fees
        }

        if (amount) {
            this.amount = amount
        }

        if (transferRequestId) {
            this.transferRequestId = transferRequestId
        }

        if (!this.fees) {
            throw new MissingSecret('Fee information is missing')
        }

        if (!this.transferRequestId) {
            throw new MissingSecret('Transfer request id is missing')
        }

        if (!this.amount) {
            throw new MissingSecret('Transfer amount is missing')
        }
    }

    /**
     * Returns the card info fit for display.
     */
    get formattedCard() {
        return formatCard(this.card)
    }

    /**
     * Total amount that will be charged on the card, including fees.
     */
    get totalAmount(): Amount {
        if (!this.fee) {
            return this.amount
        }

        return addAmounts(this.fee, this.amount)
    }

    /**
     * Returns true if this payment method has a transaction fee.
     */
    get fee(): Amount | undefined {
        if (this.card.type !== CardTypeForDisplay.Credit) return
        return this.fees.fee
    }

    /**
     * Returns the current fee info fit for display.
     */
    get formattedFee() {
        if (!this.fee) return
        return formatAmount(this.fee)
    }

    /**
     * Returns the generic fee info fit for display.
     */
    get formattedFeeSchema() {
        const {
            fee,
            schema: { maxFee, minFee, feePercent },
        } = this.fees

        const zero = formatAmount({ ...fee, value: '0' })

        const formatValue = (value?: Amount) => {
            if (!value) return ''
            const formatted = formatAmount(value)
            if (formatted === zero) return ''
            return formatted
        }

        return {
            maxFee: formatValue(maxFee),
            minFee: formatValue(minFee),
            percent: formatPercent(feePercent),
        }
    }

    /**
     * Something to be put into PersonalizationProvider
     */
    get personalizations() {
        const { formattedCard, contactInfo, formattedFee, formattedFeeSchema } =
            this

        return {
            ...formattedFeeSchema,
            maskedCardNumber: formattedCard,
            email: contactInfo.email,
            creditCardFeeFormatted: formattedFee ?? '',
            cardholderName: contactInfo.fullName ?? '',
        }
    }

    /**
     * Attempts to synchronously retrieve the existing intent from storage.
     */
    static restore(sdk: SdkClientStore) {
        try {
            return new PaymentIntentStore(sdk)
        } catch (e) {
            if (e instanceof MissingSecret) return
            throw e
        }
    }

    static async create(
        sdk: SdkClientStore,
        request: UnauthenticatedTransferRequest,
    ) {
        const api = await sdk.createPublicApi()
        const fees = await api.getCreditCardFees(request.amount)

        return new PaymentIntentStore(sdk, request.id, fees, request.amount)
    }

    /**
     * Just sets the values. Doesn't move the store to a stage.
     */

    setValues = (values: Partial<PaymentFormValues>) => {
        if ('contactInfo' in values) {
            Object.assign(this.contactInfo, values.contactInfo)
        }

        if ('addressInfo' in values) {
            Object.assign(this.addressInfo, values.addressInfo)
        }
    }

    /**
     * Just sets the values. Doesn't move the store to a stage.
     */

    setStripeAndElements = (
        cardNumberElement: StripeCardNumberElement,
        stripe: Stripe,
    ) => {
        this.stripe = { sdk: stripe, cardNumberElement }
    }

    /**
     * Moves the store to the Input stage.
     */

    start = async (router: NextRouter) => {
        await this.moveTo(PaymentIntentStage.Input, router)
    }

    /**
     * Moves the store to the Input stage.
     */

    confirm = async (router: NextRouter) => {
        const {
            addressInfo: { zipPostalCode },
            contactInfo: { fullName },
            stripe,
        } = this

        if (!stripe) {
            throw new PaymentError()
        }

        const { cardNumberElement, sdk } = stripe

        const [{ paymentMethod }, { token }] = await Promise.all([
            sdk.createPaymentMethod({
                type: 'card',
                card: cardNumberElement,
                billing_details: {
                    name: fullName,
                    address: {
                        postal_code: zipPostalCode,
                    },
                },
            }),
            sdk.createToken(cardNumberElement, {
                name: fullName,
                address_country: 'US',
                address_zip: zipPostalCode,
                currency: 'usd',
            }),
        ])

        if (!token?.card || !paymentMethod?.card) {
            throw new PaymentError(TEXT.enterCardInfo)
        }

        const {
            card: { funding, last4 },
        } = paymentMethod

        const type = [
            CardTypeForDisplay.Credit,
            CardTypeForDisplay.Debit,
            CardTypeForDisplay.Prepaid,
        ].includes(funding as CardTypeForDisplay)
            ? (funding as CardTypeForDisplay)
            : CardTypeForDisplay.Other

        if (type === CardTypeForDisplay.Other) {
            throw new PaymentError(TEXT.onlyCreditAndDebit)
        }

        this.card = {
            token: token.id,
            last4,
            type,
        }

        await this.moveTo(PaymentIntentStage.Confirmation, router)
    }

    /**
     * Finishes payment flow.
     * Moves the store to the Payment stage.
     */

    submit = async (
        router: NextRouter,
        linking: LinkingStore,
        personalizations: Personalizations,
    ): Promise<void> => {
        this.authorizationUrl = ''

        await this.moveTo(PaymentIntentStage.Payment, router)

        const {
            contactInfo: { fullName, email },
            card,
            totalAmount,
            fee,
        } = this

        if (!card) {
            throw new PaymentError()
        }

        try {
            const api = await this.sdk.createPublicApi()

            const status = await api.sendMoney(
                totalAmount,
                fee,
                { stripe: { token: card.token } },
                this.transferRequestId,
                email,
                fullName,
            )

            if (status.success) {
                await this.moveTo(PaymentIntentStage.Success, router)
            } else if (status.pending?.requires3DS) {
                this.authorizationUrl = decodeURIComponent(
                    status.pending.requires3DS.authorizationUrl,
                )
            } else if (status.pending) {
                logger.warn('Unexpected pending status:', status.pending)
                await this.moveTo(PaymentIntentStage.Success, router)
            } else {
                logger.warn('Transfer failed:', status.failure)

                if (this.attempts > 0) {
                    await this.moveTo(PaymentIntentStage.Fail, router)
                    return
                }

                this.attempts++

                const shouldRetry = await modalStore.promptById(
                    CustomModalId.PaymentFailed,
                    personalizations,
                )

                // user selected "try again" in the modal
                if (shouldRetry) {
                    await this.moveTo(PaymentIntentStage.Input, router)
                    return
                }

                // user selected "pay on step" in the modal
                if (linking.appDownloadLink) {
                    router.replace(linking.appDownloadLink)
                    return
                }

                await this.moveTo(PaymentIntentStage.Fail, router)
            }
        } catch (error) {
            captureException(error, {
                tags: { flow: 'p2p-payment' },
            })

            await this.moveTo(PaymentIntentStage.Fail, router)
        }
    }

    /**
     * Moves the store to a particular stage.
     */
    private async moveTo(stage: PaymentIntentStage, router: NextRouter) {
        const { transferRequestId } = this

        const link = LINK.paymentIntent(transferRequestId, stage).serialize()

        if (
            [
                PaymentIntentStage.Input,
                PaymentIntentStage.Confirmation,
            ].includes(stage)
        ) {
            router.push(link)
            return
        }

        router.replace(link)
    }
}

export class MissingSecret extends AssertionFailure {
    name = 'MissingSecret'
}

export class PaymentError extends Error {
    name = 'PaymentError'
}
